<template lang="pug">
  .feed-posts#feed-posts-top
    .feed-posts-header
      WtTitle.title-header(card-title-large) {{ title }}
      .feed-posts-center-toggle(v-if="isSiteWide")
        b-switch(v-model="photosOnly" @input="handleFilterChange" size="is-medium") Only Images
      .feed-posts-filters(v-if="isSiteWide")
        .field
          label.label Filters:
          .control
            .select
              select(v-model="filterMode" @change="handleFilterChange")
                option(value="all") All Posts
                option(value="friends") Friends Only

    .feed-posts-list(v-if="!_.isEmpty(feedData)")
      .feed-posts-group(v-for="group in groupedPosts" :key="group.date")
        p.group-date-header {{ group.dateLabel }}
        FeedPostCard(
          v-for="post in group.posts"
          :key="post.id"
          :post="post"
          :can-manage="canManage"
          :show-visibility="showVisibility"
          @edit="openEditModal"
          @delete="confirmDeletePost"
          :is-site-wide="isSiteWide"
        )

    .empty-state(v-else)
      p Hey there,
      br
      p {{ emptyStateText }}

    progress(max="100" v-if="isLoading === true").progress.is-small.is-primary

    BModal(
      v-show="editModalActive"
      :active.sync="editModalActive"
      has-modal-card
      :can-cancel="['escape', 'outside']"
      @close="closeEditModal"
    )
      .card-contents
        LogTrackingModal(
          v-if="editingPost"
          :key="`edit-post-${editingPost.id}`"
          :editing-post="editingPost"
          :auto-select-activity="editActivityName"
          :can-show-all-metrices="true"
          @closeActivityTracker="closeEditModal"
          @activityLogged="handlePostUpdated"
        )
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import WtTitle from "@@/WtTitle/WtTitle";
import FeedPostCard from "@@/Members/FeedPosts/FeedPostCard";
import LogTrackingModal from "@@/ActivityTracking/LogTrackingModal.vue";

const ACTIVITY_NAME_BY_METRIC = {
  steps: "Steps",
  activities: "Activities",
  "wellness:nutrition": "Nutrition",
  "wellness:water": "Water",
  "wellness:heart": "Heart Rate",
  "wellness:sleep": "Sleep",
  "wellness:weight": "Weight",
  "wellness:mindfulness": "Mindfulness",
  "wellness:mood": "Mood"
};

export default {
  name: "FeedPosts",

  components: {
    WtTitle,
    FeedPostCard,
    LogTrackingModal
  },

  props: {
    feedData: {
      type: Array,
      required: true
    },

    title: {
      type: String,
      default: "My posts"
    },

    fetchAction: {
      type: Function,
      default: () => {}
    },

    isLastPage: {
      type: Boolean,
      default: false
    },

    loaded: {
      type: Boolean,
      default: false
    },

    canManage: {
      type: Boolean,
      default: true
    },

    showVisibility: {
      type: Boolean,
      default: true
    },

    emptyStateText: {
      type: String,
      default: "Your posts will show here when data is available."
    },

    isSiteWide: {
      type: Boolean,
      default: false
    },

    organizationId: {
      type: [Number, String],
      default: null
    }
  },

  data() {
    return {
      page: 1,
      isLoading: false,
      editModalActive: false,
      editingPost: null,
      photosOnly: false,
      filterMode: "all"
    };
  },

  computed: {
    ...mapGetters(["isMobile"]),

    groupedPosts() {
      const groups = this._.groupBy(this.feedData, "postDate");

      return Object.keys(groups)
        .sort((a, b) => b.localeCompare(a))
        .map(date => ({
          date,
          dateLabel: this.moment(date).isSame(this.moment(), "day")
            ? "Today"
            : this.moment(date).format("DD MMMM"),
          // most recently updated (or created) first within the day
          posts: this._.orderBy(
            groups[date],
            post => this.moment(post.updatedAt || post.createdAt).valueOf(),
            "desc"
          )
        }));
    },

    editActivityName() {
      if (!this.editingPost) {
        return null;
      }

      const { metricType, metricText } = this.editingPost;
      const key =
        metricType === "wellness" ? `wellness:${metricText}` : metricType;

      return ACTIVITY_NAME_BY_METRIC[key] || "Post";
    }
  },

  mounted() {
    window.addEventListener("scroll", this.onScroll);
  },

  beforeDestroy() {
    window.removeEventListener("scroll", this.onScroll);
  },

  methods: {
    ...mapActions(["snackBar"]),
    ...mapActions("feed", ["deleteFeedPost"]),

    fetchItems(page = null, resetList = false) {
      if (this.isLoading) {
        return Promise.resolve();
      }
      this.isLoading = true;

      const friends = this.filterMode === "friends" ? 1 : 0;
      const photosOnly = this.photosOnly ? 1 : 0;
      return Promise.resolve(
        this.fetchAction(page, resetList, photosOnly, friends)
      ).finally(() => {
        this.isLoading = false;
      });
    },

    handleFilterChange() {
      this.page = 1;
      this.fetchItems(1, true);
    },

    openEditModal(post) {
      this.editingPost = post;
      this.editModalActive = true;
    },

    closeEditModal() {
      this.editModalActive = false;
      this.editingPost = null;
    },

    handlePostUpdated(entry) {
      this.$emit("post-updated", entry);
    },

    confirmDeletePost(post) {
      this.$buefy.dialog.confirm({
        title: "Delete post",
        message:
          "Are you sure you want to delete this post? This action cannot be undone.",
        confirmText: "Delete post",
        cancelText: "Cancel",
        type: "is-danger",
        hasIcon: true,
        onConfirm: () => this.handleDeletePost(post)
      });
    },

    async handleDeletePost(post) {
      const response = await this.deleteFeedPost({ postId: post.id });

      if (response.error) {
        this.snackBar({
          type: "is-danger",
          duration: 10000,
          queue: false,
          message: this._.get(response, "error.message", "Error deleting post.")
        });
        return;
      }

      this.snackBar({ message: "Post deleted!" });
      this.$emit("post-deleted", post);
    },

    onScroll() {
      // send requests sequentially to keep the data in sorted order
      if (this.isLoading || this.isLastPage) {
        return;
      }

      if (this.isMobile) {
        const element = document.getElementById("feed-posts-top");
        if (!element) {
          return;
        }
        const divBottom =
          element.getBoundingClientRect().bottom -
          document.documentElement.clientHeight;
        if (divBottom > 1) {
          return;
        }
      } else if (
        document.documentElement.scrollHeight -
          document.documentElement.scrollTop -
          document.documentElement.clientHeight >=
        1
      ) {
        return;
      }

      this.page += 1;
      this.fetchItems(this.page, false);
    }
  }
};
</script>

<style lang="sass" scoped>
.feed-posts
  .feed-posts-header
    display: flex
    justify-content: space-between
    align-items: center

  .title-header
    display: flex
    align-items: center

  .feed-posts-center-toggle
    display: flex
    align-items: center
    justify-self: center

    ::v-deep .field
      margin-bottom: 0
      display: flex
      align-items: center

  .feed-posts-filters
    display: flex
    gap: 1rem
    align-items: center
    justify-self: end

    .field
      margin: 0
      display: flex
      flex-direction: row
      align-items: center

    .label
      font-size: 17px
      font-weight: 600
      margin-bottom: 0
      margin-right: 0.5rem
      white-space: nowrap

    .select
      select
        font-size: 14px
        height: 36px
        min-width: 150px

  .feed-posts-list
    margin-top: 0.75rem

  .group-date-header
    font-size: 14px
    font-weight: 600
    color: $grey-dark
    margin: 0.75rem 0 0.5rem 0.25rem

  .feed-posts-group:first-child .group-date-header
    margin-top: 0

  .card-contents
    display: flex
    align-items: center
    justify-content: center
</style>
